import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchemaV2 } from 'types/pagination';
import { z } from 'zod/v4';

const URL = '/reasons';

export const ReasonTypeSchema = z.enum(['pause', 'dispute', 'unrecoverable']);
export type ReasonType = z.infer<typeof ReasonTypeSchema>;

export const ReasonSchema = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  reasonType: ReasonTypeSchema,
  isDeletable: z.boolean(),
});

export type Reason = z.infer<typeof ReasonSchema>;

const LoadReasonsResponseSchema = z.object({
  data: z.array(ReasonSchema),
  pagination: PaginationSchemaV2,
});

export type LoadReasonsResponse = z.infer<typeof LoadReasonsResponseSchema>;

export type LoadReasonsParams = {
  reasonType?: ReasonType;
};

export async function loadReasonsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  params: LoadReasonsParams,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params });

  const parseResult = LoadReasonsResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'reasons');
    throw new SilentError();
  }

  return parseResult.data;
}

const EMPTY_REASONS: LoadReasonsResponse = {
  data: [],
  pagination: { totalItems: 0 },
};

export function useLoadReasons(
  params: LoadReasonsParams = {},
  options: QueryOptions<LoadReasonsResponse> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LoadReasonsResponse, ApiError>({
    queryKey: ['reasons', params],
    queryFn: () => loadReasonsQueryFn(axiosInstance, params),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return EMPTY_REASONS;
    },
    ...options,
  });

  const result = addResourceNameToQueryResult<LoadReasonsResponse, ApiError, 'reasons'>(
    'reasons',
    queryResult,
  );

  // placeholderData only applies while the query is pending: if the request fails before ever
  // succeeding, `reasons` becomes undefined even though every consumer (the reason forms'
  // reasons.data.map, mark-as-unrecoverable, use-pause-with-note, Reasons) accesses it unguarded
  // and would crash on render. The fallback keeps `reasons` defined in that error state — the
  // failure itself stays observable via reasonsError.
  return { ...result, reasons: result.reasons ?? EMPTY_REASONS };
}
